/**
 * MultipleChoiceUI Component
 *
 * Renders an interactive multiple choice UI for LLM follow-up questions.
 * Supports single select, multi-select, and custom answers.
 */

import React, { useState, useCallback } from "react";
import { MultipleChoiceData } from "../../DocumentAgentLG/DocumentAgentLGTypes";

// ============================================================================
// TYPES
// ============================================================================

export interface MultipleChoiceUIProps {
  /** The multiple choice data from the parsed message */
  data: MultipleChoiceData;
  /** Whether this choice has already been answered */
  isAnswered: boolean;
  /** The answer that was selected (for display when answered) */
  answeredValue?: string;
  /** Callback when user selects an answer */
  onSelect: (choiceId: string, selectedLabels: string[]) => void;
  /** If true, auto-submit when a single option is clicked (for single-select questions) */
  autoSubmit?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const MultipleChoiceUI: React.FC<MultipleChoiceUIProps> = ({
  data,
  isAnswered,
  answeredValue,
  onSelect,
  autoSubmit = false,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [customValue, setCustomValue] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);

  const {
    id,
    question,
    options,
    allowMultiple,
    allowCustom,
    customPlaceholder,
  } = data;

  // Handle option click
  const handleOptionClick = useCallback(
    (optionId: string, optionLabel: string) => {
      if (isAnswered) return;

      if (allowMultiple) {
        // Toggle selection for multi-select
        setSelectedIds(prev => {
          const next = new Set(prev);
          if (next.has(optionId)) {
            next.delete(optionId);
          } else {
            next.add(optionId);
          }
          return next;
        });
      } else {
        // Single select - select this option (replacing any previous selection)
        setSelectedIds(new Set([optionId]));
        
        // Auto-submit for single-select if enabled
        if (autoSubmit && !allowMultiple) {
          onSelect(id, [optionLabel]);
        }
      }
      setShowCustomInput(false);
    },
    [isAnswered, allowMultiple, autoSubmit, id, onSelect]
  );

  // Handle custom input toggle
  const handleCustomToggle = useCallback(() => {
    if (isAnswered) return;
    if (allowMultiple) {
      // For multi-select, just toggle the custom input visibility
      setShowCustomInput(prev => !prev);
    } else {
      // For single-select, show custom and clear selections
      setShowCustomInput(true);
      setSelectedIds(new Set());
    }
  }, [isAnswered, allowMultiple]);

  // Handle custom input change
  const handleCustomChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setCustomValue(e.target.value);
    },
    []
  );

  // Handle submit (for multi-select or custom)
  const handleSubmit = useCallback(() => {
    if (isAnswered) return;

    // Collect selected option labels
    const selectedLabels = options
      .filter(opt => selectedIds.has(opt.id))
      .map(opt => opt.label);

    // For multi-select, combine selected options with custom answer
    if (allowMultiple) {
      const allAnswers = [...selectedLabels];
      if (showCustomInput && customValue.trim()) {
        allAnswers.push(customValue.trim());
      }
      if (allAnswers.length > 0) {
        onSelect(id, allAnswers);
      }
    } else {
      // For single-select, it's either custom or a selected option
      if (showCustomInput && customValue.trim()) {
        onSelect(id, [customValue.trim()]);
      } else if (selectedLabels.length > 0) {
        onSelect(id, selectedLabels);
      }
    }
  }, [
    isAnswered,
    allowMultiple,
    showCustomInput,
    customValue,
    selectedIds,
    options,
    id,
    onSelect,
  ]);

  // Handle enter key in custom input
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && customValue.trim()) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [customValue, handleSubmit]
  );

  // Determine if submit button should be shown
  // - Auto-submit single-select: show button only for custom input
  // - Multi-select or regular mode: show button when there's any selection
  const showSubmitButton =
    !isAnswered &&
    ((autoSubmit && !allowMultiple && showCustomInput && customValue.trim()) ||
     (autoSubmit && allowMultiple && (selectedIds.size > 0 || (showCustomInput && customValue.trim()))) ||
     (!autoSubmit && (selectedIds.size > 0 || (showCustomInput && customValue.trim()))));

  return (
    <div
      className={`multiple-choice-container ${isAnswered ? "answered" : ""}`}
    >
      <div className="multiple-choice-question">{question}</div>

      {isAnswered ? (
        // Show answered state
        <div className="multiple-choice-answered">
          <span className="answered-label">Your answer:</span>
          <span className="answered-value">{answeredValue}</span>
        </div>
      ) : (
        // Show interactive options
        <>
          <div className="multiple-choice-options">
            {options.map((option, index) => {
              const isSelected = selectedIds.has(option.id);
              const letter = String.fromCharCode(65 + index); // A, B, C, ...
              return (
                <button
                  key={option.id}
                  className={`multiple-choice-option ${isSelected ? "selected" : ""}`}
                  onClick={() => handleOptionClick(option.id, option.label)}
                  disabled={isAnswered}
                  type="button"
                >
                  <span className="option-letter">{letter}.</span>
                  {allowMultiple && (
                    <span className="checkbox-indicator">
                      {isSelected ? "☑" : "☐"}
                    </span>
                  )}
                  <span className="option-label">{option.label}</span>
                </button>
              );
            })}

            {allowCustom && (
              <button
                className={`multiple-choice-option custom-option ${showCustomInput ? "selected" : ""}`}
                onClick={handleCustomToggle}
                disabled={isAnswered}
                type="button"
              >
                <span className="option-letter">
                  {String.fromCharCode(65 + options.length)}.
                </span>
                <span className="option-label">Other...</span>
              </button>
            )}
          </div>

          {showCustomInput && (
            <div className="multiple-choice-custom">
              <input
                type="text"
                className="custom-input"
                placeholder={customPlaceholder || "Type your answer..."}
                value={customValue}
                onChange={handleCustomChange}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>
          )}

          {showSubmitButton && (
            <div className="multiple-choice-submit">
              <button
                className="submit-button"
                onClick={handleSubmit}
                type="button"
              >
                Submit
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MultipleChoiceUI;
